// Contains some utility functions that are used in a couple of places in Signals

import { AuthStore } from "stores/AuthStore";
import {
    ISignalsDashboard,
    SignalDataEndpoint,
    SignalsEndpoints,
    getIsContactReport,
    getIsDriverReport,
    getIsThemeReport,
    getIsTopicReport,
    getIsTrendReport,
} from "./SignalsModels";
import { Location } from "react-router";
import theme from "Theme/AppTheme";

export const defaultShadowsForSignals = theme.shadows[1];

export const getDefaultDashboardPdfFileName = (dashboardTitle: string) =>
    `${dashboardTitle.split(" ").join("_")}.pdf`;

/**
 * Filters the signals in the dashboard based on the user's permissions.
 * @param dashboard The dashboard to filter. Should be an ISignalsDashboard OBJECT not the DashboardStore.
 * @param authStore A reference to the authStore so we can check user permissions.
 * @returns Returns the signals objects that the user has permission to view.
 * @see SignalsPageStore.loadDashboards for usage
 */
export const filterByPerms = (
    dashboard: ISignalsDashboard,
    authStore: AuthStore,
) => {
    let newArr = [...dashboard.signalsObjects];
    newArr = newArr.filter((viz) => {
        if (
            !authStore.canUserView("Topics") &&
            !!viz.dataEndpoint &&
            getIsTopicReport(viz.dataEndpoint)
        ) {
            return false;
        }
        if (
            !authStore.canUserView("Drivers") &&
            !!viz.dataEndpoint &&
            getIsDriverReport(viz.dataEndpoint)
        ) {
            return false;
        }
        // Theme Cards read the Primary Driver's Theme, so they follow Drivers rather than Topics.
        if (
            !authStore.canUserView("Drivers") &&
            !!viz.dataEndpoint &&
            getIsThemeReport(viz.dataEndpoint)
        ) {
            return false;
        }
        if (
            !authStore.canUserView("Trends") &&
            !!viz.dataEndpoint &&
            getIsTrendReport(viz.dataEndpoint)
        ) {
            return false;
        }

        if (
            !authStore.canUserView("Contacts") &&
            !!viz.dataEndpoint &&
            getIsContactReport(viz.dataEndpoint)
        ) {
            return false;
        }

        if (
            !authStore.canUserView("HIPAA Compliance Model") &&
            viz.dataEndpoint ===
                SignalsEndpoints.trends.HipaaCompliancePrevalenceOverTimeId
        ) {
            return false;
        }

        if (
            !authStore.canUserView("Safety Events Model") &&
            (viz.dataEndpoint ===
                SignalsEndpoints.trends.SEPrevalenceOverTimeID ||
                viz.dataEndpoint ===
                    SignalsEndpoints.additionalReports.LillySafetyEventTypesId)
        ) {
            return false;
        }

        if (
            !authStore.canUserView("Patient Journey") &&
            viz.dataEndpoint ===
                SignalsEndpoints.additionalReports.JourneyStageCountId
        ) {
            return false;
        }

        if (
            !authStore.canUserView("Patient Journey") &&
            viz.dataEndpoint ===
                SignalsEndpoints.additionalReports.JourneyStageAndEddyEffectId
        ) {
            return false;
        }

        if (!authStore.canUserView("Module Scores")) {
            const moduleScoreGraphs = [
                SignalsEndpoints.trends.AverageScoreByModuleOverTimeID,
                SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID,
                SignalsEndpoints.trends.QualityScoreOverTimeID,
                SignalsEndpoints.trends.AverageScoreBySkillID,
                SignalsEndpoints.eddyReports.AverageScoreByQuestionID,
                SignalsEndpoints.eddyReports
                    .AverageScoreByQuestionAndEddyOverTimeID,
            ] as SignalDataEndpoint[];
            if (
                moduleScoreGraphs.includes(
                    viz.dataEndpoint as SignalDataEndpoint,
                )
            ) {
                return false;
            }
        }

        if (
            authStore.canUserView("Restrict Evaluations") &&
            !authStore.isUserUltra()
        ) {
            const evaluationGraphs = [
                SignalsEndpoints.eddyReports.AverageScoreByQuestionID,
                SignalsEndpoints.eddyReports
                    .AverageScoreByQuestionAndEddyOverTimeID,
                SignalsEndpoints.eddyReports.EEPrevalenceByQuestionID,
                SignalsEndpoints.eddyReports.EEPrevalenceByQuestionOverTimeID,
                SignalsEndpoints.additionalReports.CountByQuestionResponseId,
                SignalsEndpoints.additionalReports.ModuleResultsCountId,
                SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID,
                SignalsEndpoints.trends.AverageScoreByModuleOverTimeID,
                SignalsEndpoints.trends.QualityScoreOverTimeID,
                SignalsEndpoints.trends.AverageScoreBySkillID,
            ] as SignalDataEndpoint[];
            if (
                evaluationGraphs.includes(
                    viz.dataEndpoint as SignalDataEndpoint,
                )
            ) {
                return false;
            }
        }

        const modelGraphs = [
            SignalsEndpoints.additionalReports.ModelResultsCountId,
            SignalsEndpoints.additionalReports.ModelResultsOverTimeId,
            SignalsEndpoints.additionalReports.ExtractedConversationDataId,
            SignalsEndpoints.additionalReports.GroupedModelResultsId,
        ] as SignalDataEndpoint[];
        if (
            !authStore.canUserView("Model Results") &&
            (modelGraphs.includes(viz.dataEndpoint as SignalDataEndpoint) ||
                viz.dataEndpoint?.includes(
                    SignalsEndpoints.additionalReports
                        .PublishedModelResultsCountId,
                ) ||
                viz.dataEndpoint?.includes(
                    SignalsEndpoints.additionalReports
                        .PublishedModelResultsOverTimeId,
                ) ||
                viz.dataEndpoint?.includes(
                    SignalsEndpoints.additionalReports
                        .PublishedGroupedModelResultsId,
                ))
        ) {
            return false;
        }

        return true;
    });

    return newArr;
};

/**
 * Tracks what dashboards are being set as home pages through pendo
 * @param dashboardId The ID of the dashboard being viewed.
 * @param dashboardTitle The title of the dashboard being viewed.
 * @param pathname The current pathname of the application.
 */
export function trackHomePage(
    dashboardId?: string,
    dashboardTitle?: string,
    pathname?: string,
) {
    const isDev = process.env.NODE_ENV === "development";
    if (isDev) {
        return;
    }

    if (!dashboardId || !dashboardTitle || !pathname) {
        return;
    }
    const pendoInstance = (window as { pendo?: { track?: Function } }).pendo;
    if (!pendoInstance?.track) {
        return;
    }

    pendoInstance.track("Set Home Page", {
        dashboard_id: dashboardId, // Unique ID of the selected dashboard
        dashboard_name: dashboardTitle, // Friendly name of the dashboard
        set_from_page: pathname, // URL or source page where the action happened
        timestamp: new Date().toISOString(), // UTC timestamp when the action was taken
    });
}

/**
 * Checks if the current location is within the reports section of Signals ('reports' or 'reporting').
 * @param location The current location object from react-router.
 * @returns True if the location is within reports, false otherwise.
 */
export function isReportsLocation(location: Location): boolean {
    return (
        location.pathname.includes("reporting") ||
        location.pathname.includes("reports")
    );
}

/**
 * Waits for a specified amount of time and can be aborted using an AbortSignal.
 * @param ms The number of milliseconds to wait.
 * @param signal An optional AbortSignal to cancel the wait.
 * @returns A promise that resolves after the wait or rejects if aborted.
 */
export function wait(ms, signal) {
    return new Promise((resolve, reject) => {
        // If a signal is provided and already aborted, reject immediately
        if (signal && signal.aborted) {
            return reject(new DOMException("Aborted", "AbortError"));
        }

        let timeoutId;

        const onAbort = () => {
            clearTimeout(timeoutId); // Clear the pending timeout
            reject(new DOMException("Aborted", "AbortError")); // Reject the promise
            signal.removeEventListener("abort", onAbort); // Clean up the event listener
        };

        if (signal) {
            signal.addEventListener("abort", onAbort);
        }

        timeoutId = setTimeout(() => {
            if (signal) {
                signal.removeEventListener("abort", onAbort); // Clean up on successful completion
            }
            resolve(void 0); // Resolve the promise after the delay
        }, ms);
    });
}
